import React from 'react';
import { UseFormReturn } from 'react-hook-form';
import {
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
  FormDescription,
} from '@/components/ui/form';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Switch } from '@/components/ui/switch';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { CampoTipoAtivo } from '@/types/tipo-ativo';
import { AtivoFormData } from '@/types/ativo';
import { cn } from '@/lib/utils';

interface DynamicCustomFieldProps {
  campo: CampoTipoAtivo;
  form: UseFormReturn<AtivoFormData>;
}

export const DynamicCustomField = ({ campo, form }: DynamicCustomFieldProps) => {
  const fieldPath = `atributos_customizados.${campo.nome_campo}` as any;
  
  // Regras de validação para campos obrigatórios
  const validationRules = campo.obrigatorio 
    ? {
        required: `${campo.label} é obrigatório`,
        validate: (value: any) => {
          if (campo.tipo_campo === 'boolean') return true;
          if (value === undefined || value === null || value === '') {
            return `${campo.label} é obrigatório`;
          }
          return true;
        }
      }
    : undefined;

  const renderFieldByType = (field: any, hasError: boolean) => {
    const baseInputClassName = cn(
      hasError && "border-destructive focus-visible:ring-destructive"
    );
    switch (campo.tipo_campo) {
      case 'text':
      case 'email':
      case 'url':
        return (
          <Input 
            type={campo.tipo_campo} 
            placeholder={campo.configuracoes?.placeholder || ''}
            className={baseInputClassName}
            {...field}
          />
        );
      
      case 'textarea':
        return (
          <Textarea 
            placeholder={campo.configuracoes?.placeholder || ''}
            rows={4}
            className={cn("resize-none", baseInputClassName)}
            {...field}
          />
        );
      
      case 'number':
        return (
          <div className="flex items-center gap-2">
            {campo.configuracoes?.prefixo && (
              <span className="text-sm text-muted-foreground">{campo.configuracoes.prefixo}</span>
            )}
            <Input 
              type="number"
              min={campo.configuracoes?.min}
              max={campo.configuracoes?.max}
              placeholder={campo.configuracoes?.placeholder || ''}
              className={baseInputClassName}
              {...field}
              onChange={(e) => field.onChange(e.target.value ? parseFloat(e.target.value) : undefined)}
            />
            {campo.configuracoes?.sufixo && (
              <span className="text-sm text-muted-foreground">{campo.configuracoes.sufixo}</span>
            )}
          </div>
        );
      
      case 'date':
        return (
          <Input 
            type="date" 
            className={baseInputClassName}
            {...field} 
          />
        );
      
      case 'boolean':
        return (
          <Switch
            checked={field.value || false}
            onCheckedChange={field.onChange}
          />
        );
      
      case 'select':
        return (
          <Select onValueChange={field.onChange} value={field.value || ''}>
            <SelectTrigger className={hasError ? "border-destructive" : ""}>
              <SelectValue placeholder="Selecione..." />
            </SelectTrigger>
            <SelectContent>
              {campo.configuracoes?.opcoes?.map(opcao => (
                <SelectItem key={opcao} value={opcao}>
                  {opcao}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        );
      
      case 'multiselect':
        return (
          <Select onValueChange={field.onChange} value={field.value || ''}>
            <SelectTrigger className={hasError ? "border-destructive" : ""}>
              <SelectValue placeholder="Selecione (múltiplo)..." />
            </SelectTrigger>
            <SelectContent>
              {campo.configuracoes?.opcoes?.map(opcao => (
                <SelectItem key={opcao} value={opcao}>
                  {opcao}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        );
      
      default:
        return (
          <Input 
            placeholder={campo.configuracoes?.placeholder || ''} 
            className={baseInputClassName}
            {...field} 
          />
        );
    }
  };

  return (
    <FormField
      control={form.control}
      name={fieldPath}
      rules={validationRules}
      render={({ field, fieldState }) => {
        const hasError = !!fieldState.error;
        
        return (
          <FormItem>
            <FormLabel className={cn(hasError && "text-destructive")}>
              {campo.label}
              {campo.obrigatorio && <span className="text-destructive ml-1">*</span>}
            </FormLabel>
            {campo.descricao && (
              <FormDescription className="text-xs">
                {campo.descricao}
              </FormDescription>
            )}
            <FormControl>
              {renderFieldByType(field, hasError)}
            </FormControl>
            <FormMessage />
          </FormItem>
        );
      }}
    />
  );
};
